import React, { useState } from "react";
import { getBrandName } from "../getBrandName";

export const useDataRetention = () => {
  const [active, setActive] = useState("definitions");
  const { brandName , normalizedBrand } = getBrandName();

  const sections = [
    { id: "definitions", title: "How Long Do We Retain Your Data?" },
    { id: "eligibility", title: "Requesting Data Deletion" },
    { id: "retention", title: "Retention of Data for Inactive Accounts" },
    { id: "thirdparty", title: "Third-Party Integrations and External Storage" },
    { id: "backup", title: "Data Storage & Backup Retention" },
    { id: "changes", title: "Changes to This Policy" },
    { id: "contact", title: "Contact Us" },
  ];

  const handleScrollTo = (id) => {
    setActive(id);
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const generateContent = (id) => {
    const contentMap = {
      definitions: `

a. Operational Necessity
• To deliver our core services, including analytics reports, dashboards, insights, and account access.
• To maintain service continuity, backups, and support history.
• To offer a seamless experience when you return to the platform.

b. Legal & Regulatory Compliance
• Meet legal, tax, accounting, or regulatory requirements under applicable Indian and international laws (e.g., IT Act, GDPR if applicable)
• Cooperate with lawful investigations or legal processes
• Retain transaction or payment history as required by financial regulations

c. Enforcement & Dispute Resolution
• To detect, investigate, or prevent fraud, abuse, or security threats
• To resolve disputes and enforce our Terms & Conditions or legal agreements

d. Data Minimization Policy
• When the data is no longer required for the purposes above:
  • It will be securely deleted, or
  • Anonymized in such a way that it can no longer be associated with you or your business.
`,

      eligibility: `

a. How to Request Data Deletion
• Email us at ${brandName.toLowerCase()}@gmail.com with the subject: Data Deletion Request.
• Include identifying details (e.g., name, registered email, contact number, & brand name) so we can verify your identity securely.

b. Our Process
• Identity verification will be carried out to ensure data is not deleted unlawfully.
• We will evaluate your request based on retention obligations and legal limitations.
• If approved, we will delete or anonymize your data within 30 working days.
• You will receive a confirmation email upon successful deletion.

c. Important Notes
• Some data (e.g., billing records, compliance logs) may be retained temporarily to fulfill financial or legal obligations.
• Data deletion is permanent – once processed, your data and any associated analytics cannot be restored.
• If you have subscribed to services or uploaded third-party data, their deletion may be governed by separate terms.
`,

      retention: `

• For accounts that have been inactive (no login or usage) for a prolonged period:
  • We may contact you before initiating archival or deletion.
  • After 24 months of inactivity, we may anonymize or purge the data, unless otherwise required by law.
  • You will be notified at least 30 days in advance before permanent deletion.
`,

      thirdparty: `

• If you have connected third-party platforms (e.g., Amazon, Shopify, analytics tools), we do not control their retention policies.
• Deletion of data from ${brandName} does not guarantee deletion from linked platforms.
• Please refer to those services’ own data policies to manage retention independently.
`,

      backup: `

• Your data may be stored on secure cloud infrastructure hosted within India or in compliance with cross-border data policies.
• Backups may be retained for 30 to 90 days for disaster recovery purposes.
• Deleted data from live systems will also be purged from backup systems within the retention window.
`,

      changes: `

• ${brandName} may update this Data Retention & Deletion Policy from time to time to reflect changes in technology, regulations, or business practices.
• If changes are significant:
  • We will post the revised policy with a new “Last Updated” date.
  • In some cases, we may notify you directly via email or platform notification.
`,

      contact: `

• Email: ${normalizedBrand}@gmail.com  
• Contact: +91- 94248 47009  
• Website: www.${normalizedBrand}.com
`,
    };

    const lines = contentMap[id]?.split("\n") || [];

    let items = [];
    const contentElements = [];

    const flushList = () => {
      if (items.length) {
        contentElements.push(
          <ul key={Math.random()}>
            {items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        );
        items = [];
      }
    };

    lines.forEach((line, index) => {
      if (!line.trim()) return;

      if (/^\d+\.\d+/.test(line)) {
        flushList();
        contentElements.push(
          <h6 key={`subheading-${index}`} style={{ marginTop: "1rem" }}>
            {line}
          </h6>
        );
      } else if (/^•/.test(line)) {
        items.push(line.replace(/^•\s*/, ""));
      } else {
        flushList();
        contentElements.push(
          <p key={`para-${index}`} style={{ marginBottom: "0.5rem" }}>
            {line}
          </p>
        );
      }
    });

    flushList();
    return contentElements;
  };

  return { sections, active, handleScrollTo, generateContent };
};
